'use client';

// App-wide DM realtime + unread surfacing.
//
// Why this exists: previously the ONLY realtime subscription lived inside an
// open thread (app/messages/[conversationId]/page.tsx, the `dm-${id}` channel),
// so a recipient never learned about a new DM unless that exact thread was
// already open. This provider mounts once at the app root (inside AuthProvider)
// and subscribes app-wide so the nav badge and the inbox update live.
//
// Unread storage: server-side dm_read_receipts is the source of truth. Unread is
// computed by computeDmUnread() — shared with the home "Unread DMs" tile so the
// two agree by construction — and reads are persisted via the
// mark_conversation_as_read RPC when a thread is opened. (This replaced an
// earlier localStorage "last seen" model that diverged from the home tile.)
//
// Scoping: Realtime respects RLS. The dm_messages / dm_participants SELECT
// policies only expose rows in conversations the user participates in, so we
// receive events only for our own conversations — no manual conversation
// filters are applied here, by design.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { RealtimeChannel } from '@supabase/supabase-js';
import { useAuthContext } from '@/components/providers/AuthProvider';
import { createClient } from '@/lib/supabase/browser';
import { computeDmUnread } from '@/lib/dm/unreadCount';

export type DmMessageRow = {
  id: string;
  conversation_id: string;
  sender_id: string;
  content: string | null;
  created_at: string;
  message_type?: string | null;
  media_url?: string | null;
};

export type DmRealtimeEvent =
  | { type: 'message'; row: DmMessageRow }
  | { type: 'participant'; conversationId: string };

type DmEventListener = (event: DmRealtimeEvent) => void;

interface DmRealtimeContextValue {
  /** Per-conversation unread message count for the current user. */
  unreadByConversation: Record<string, number>;
  /** Total unread messages across all conversations. */
  totalUnread: number;
  /**
   * True once the initial receipts-based seed has completed. Consumers that also
   * have a server-rendered seed value (the home "Unread DMs" tile) can show that
   * SSR value until `ready`, then adopt the live provider value without a
   * 0-flash while the async seed is in flight.
   */
  ready: boolean;
  /** Mark a conversation read now (clears its unread + persists last-seen). */
  markConversationRead: (conversationId: string) => void;
  /**
   * Declare which conversation is currently on screen (or null). While a
   * conversation is active, incoming messages for it are treated as read so the
   * user isn't badged for a thread they're looking at.
   */
  setActiveConversation: (conversationId: string | null) => void;
  /** Subscribe to raw DM events (used by the inbox to stay live). Returns an unsubscribe fn. */
  onDmEvent: (listener: DmEventListener) => () => void;
}

const DmRealtimeContext = createContext<DmRealtimeContextValue | undefined>(undefined);

export function DmRealtimeProvider({ children }: { children: ReactNode }) {
  const { user } = useAuthContext();
  const supabase = useMemo(() => createClient(), []);

  const [unreadByConversation, setUnreadByConversation] = useState<Record<string, number>>({});
  const [ready, setReady] = useState(false);

  const userIdRef = useRef<string | null>(null);
  const activeConversationRef = useRef<string | null>(null);
  const listenersRef = useRef<Set<DmEventListener>>(new Set());

  userIdRef.current = user?.id ?? null;

  const emit = useCallback((event: DmRealtimeEvent) => {
    listenersRef.current.forEach((listener) => {
      try {
        listener(event);
      } catch {
        // A bad listener must not break realtime delivery for the others.
      }
    });
  }, []);

  const onDmEvent = useCallback((listener: DmEventListener) => {
    listenersRef.current.add(listener);
    return () => {
      listenersRef.current.delete(listener);
    };
  }, []);

  // Persist read-state server-side: writes dm_read_receipts for every message in
  // the conversation not sent by the caller. Idempotent, so safe to call often.
  //
  // Write-then-confirm: we AWAIT the RPC and retry once on failure. The caller
  // only clears the unread badge on a confirmed write — a failed persist leaves
  // the count intact, because a badge that stays visible is the honest state
  // until the read actually lands in dm_read_receipts. (Previously this was a
  // fire-and-forget `void supabase.rpc(...)`; the badge cleared optimistically
  // even when the receipt never persisted, so the home tile — which reads the DB
  // truth — could disagree with the inbox indefinitely.)
  const persistConversationRead = useCallback(
    async (conversationId: string): Promise<boolean> => {
      const id = userIdRef.current;
      if (!conversationId || !id) return false;
      for (let attempt = 0; attempt < 2; attempt += 1) {
        const { error } = await supabase.rpc('mark_conversation_as_read', {
          p_conversation_id: conversationId,
          p_user_id: id,
        });
        if (!error) return true;
      }
      return false;
    },
    [supabase],
  );

  // After a confirmed persist, reconcile the conversation's in-memory count to
  // the DB truth via computeDmUnread instead of assuming 0 — so any message that
  // arrived between the persist and now is still counted.
  const reconcileConversation = useCallback(
    async (conversationId: string) => {
      const id = userIdRef.current;
      if (!id) return;
      try {
        const { byConversation } = await computeDmUnread(supabase, id);
        setUnreadByConversation((prev) => ({
          ...prev,
          [conversationId]: byConversation[conversationId] ?? 0,
        }));
      } catch {
        // If reconciliation fails, leave the in-memory count as-is rather than
        // guessing — the next seed will correct it.
      }
    },
    [supabase],
  );

  const markConversationRead = useCallback(
    (conversationId: string) => {
      if (!conversationId) return;
      void (async () => {
        const ok = await persistConversationRead(conversationId);
        if (ok) await reconcileConversation(conversationId);
      })();
    },
    [persistConversationRead, reconcileConversation],
  );

  // Debounced, visibility-gated read for the CURRENTLY-OPEN thread. Fired when a
  // message arrives for the active conversation (or when the tab returns to the
  // foreground with a thread open). Without this, a message that lands while the
  // recipient already has the thread open is shown and de-badged but never
  // receipted, so the next session seed counts it as phantom-unread.
  //   • Debounced: a burst of messages collapses to a single RPC.
  //   • Visibility-gated: a message that arrives while the tab is backgrounded is
  //     NOT marked read — it stays unread until the thread is actually viewed
  //     again (the visibilitychange handler below catches that).
  // mark_conversation_as_read is idempotent and covers everything unreceipted in
  // the thread, so one call suffices regardless of how many messages arrived.
  //
  // Durability: the pending conversation id is tracked in a ref so a tab-close or
  // unmount inside the 350ms debounce window can flush it (see flushPendingActiveRead)
  // rather than silently dropping the receipt. The pending id is cleared only
  // once its persist is confirmed.
  const markReadTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingActiveReadRef = useRef<string | null>(null);

  const runActiveReadPersist = useCallback(
    async (conversationId: string) => {
      const ok = await persistConversationRead(conversationId);
      if (ok) {
        if (pendingActiveReadRef.current === conversationId) {
          pendingActiveReadRef.current = null;
        }
        await reconcileConversation(conversationId);
      }
      // On failure keep pendingActiveReadRef set so a later flush retries and the
      // badge stays visible until the read persists.
    },
    [persistConversationRead, reconcileConversation],
  );

  const scheduleActiveRead = useCallback(
    (conversationId: string) => {
      if (!conversationId) return;
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      pendingActiveReadRef.current = conversationId;
      if (markReadTimerRef.current) clearTimeout(markReadTimerRef.current);
      markReadTimerRef.current = setTimeout(() => {
        markReadTimerRef.current = null;
        void runActiveReadPersist(conversationId);
      }, 350);
    },
    [runActiveReadPersist],
  );

  // Flush a pending debounced read immediately (used on unmount / tab-hide /
  // beforeunload). Cancels the timer and fires the persist now so a read caught
  // inside the debounce window isn't lost when the tab goes away.
  const flushPendingActiveRead = useCallback(() => {
    const conversationId = pendingActiveReadRef.current;
    if (!conversationId) return;
    if (markReadTimerRef.current) {
      clearTimeout(markReadTimerRef.current);
      markReadTimerRef.current = null;
    }
    void runActiveReadPersist(conversationId);
  }, [runActiveReadPersist]);

  const setActiveConversation = useCallback(
    (conversationId: string | null) => {
      activeConversationRef.current = conversationId;
      if (conversationId) markConversationRead(conversationId);
    },
    [markConversationRead],
  );

  // Seed unread counts + subscribe app-wide for the signed-in user.
  useEffect(() => {
    const userId = user?.id;
    if (!userId) {
      setUnreadByConversation({});
      setReady(false);
      return;
    }

    let cancelled = false;

    const seed = async () => {
      // Receipts-based unread, shared with the home tile via computeDmUnread.
      const { byConversation } = await computeDmUnread(supabase, userId);
      if (!cancelled) {
        setUnreadByConversation(byConversation);
        setReady(true);
      }
    };

    void seed();

    const channel: RealtimeChannel = supabase
      .channel(`dm-inbox-${userId}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'dm_messages' },
        (payload: { new: Record<string, unknown> }) => {
          const row = payload.new as unknown as DmMessageRow;
          if (!row?.id || !row.conversation_id) return;

          // Always notify the inbox so it can reorder + refresh the preview,
          // including for the user's own sent messages.
          emit({ type: 'message', row });

          if (row.sender_id === userIdRef.current) return; // own message: never unread
          if (row.conversation_id === activeConversationRef.current) {
            // User is looking at this thread — suppress the live badge and
            // persist a receipt (debounced, only while the tab is visible)
            // instead of badging.
            scheduleActiveRead(row.conversation_id);
            return;
          }

          setUnreadByConversation((prev) => ({
            ...prev,
            [row.conversation_id]: (prev[row.conversation_id] || 0) + 1,
          }));
        },
      )
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'dm_participants' },
        (payload: { new: Record<string, unknown> }) => {
          const row = payload.new as { conversation_id?: string; user_id?: string };
          if (!row?.conversation_id) return;
          // RLS only delivers participant rows for conversations we belong to,
          // so this fires when a brand-new conversation we're part of appears.
          emit({ type: 'participant', conversationId: row.conversation_id });
        },
      )
      .subscribe();

    return () => {
      cancelled = true;
      void supabase.removeChannel(channel);
    };
  }, [user?.id, supabase, emit, scheduleActiveRead]);

  // When the tab returns to the foreground with a thread open, mark it read.
  // This catches messages that arrived while the tab was backgrounded (which
  // scheduleActiveRead deliberately does NOT receipt at arrival time). When the
  // tab is hidden or unloading, flush any pending debounced read so a receipt
  // caught inside the 350ms window isn't dropped on tab-close.
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        if (activeConversationRef.current) scheduleActiveRead(activeConversationRef.current);
      } else {
        flushPendingActiveRead();
      }
    };
    const onBeforeUnload = () => {
      flushPendingActiveRead();
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, [scheduleActiveRead, flushPendingActiveRead]);

  // Flush any pending debounced read on unmount so it isn't silently dropped.
  useEffect(() => {
    return () => {
      flushPendingActiveRead();
    };
  }, [flushPendingActiveRead]);

  const totalUnread = useMemo(
    () => Object.values(unreadByConversation).reduce((sum, n) => sum + (n || 0), 0),
    [unreadByConversation],
  );

  const value = useMemo<DmRealtimeContextValue>(
    () => ({
      unreadByConversation,
      totalUnread,
      ready,
      markConversationRead,
      setActiveConversation,
      onDmEvent,
    }),
    [unreadByConversation, totalUnread, ready, markConversationRead, setActiveConversation, onDmEvent],
  );

  return <DmRealtimeContext.Provider value={value}>{children}</DmRealtimeContext.Provider>;
}

export function useDmRealtime(): DmRealtimeContextValue {
  const ctx = useContext(DmRealtimeContext);
  if (ctx === undefined) {
    throw new Error('useDmRealtime must be used within a DmRealtimeProvider');
  }
  return ctx;
}
